/**
 * Day 83 P6.2 — Provenance tooltip + indicator wrapper.
 *
 * Composable wrapper that renders children + provenance indicators (italic,
 * stale icon/chip) + hover tooltip with source / timestamp / override chain /
 * reason. Three modes per L8 stale-indicator-by-surface guidance:
 *
 *   - 'inline'  ProductDetail KV row pattern — italic on children + Clock icon
 *                adjacent when stale + hover tooltip
 *   - 'cell'    AG Grid cell pattern — italic on children + Clock icon when
 *                stale + native HTML `title=` attribute (no custom hover
 *                listeners; required for grid perf at scale)
 *   - 'chip'    Directory card view pattern — italic on children + "Stale"
 *                chip when stale + hover tooltip
 *
 * R3 Sol-lock: `suppressStale` prop omits stale chip/icon while still
 * rendering source-prefix + relative timestamp in tooltip body. Used by
 * ProductDetail Section 7 algopix_data wrap (Algopix has its own 3-day
 * data_pulled_at staleness semantic, not the 90-day manual-override default).
 */

import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { Clock } from 'lucide-react';
import clsx from 'clsx';
import { deriveProvenanceIndicator, sourcePrefix, type ProvenanceMap } from './ProvenanceIndicator';

function relativeTimeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  const days = Math.floor(ms / 86_400_000);
  if (days <= 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function defaultPreviousValueFormatter(value: unknown): string {
  if (value === null || value === undefined) return '(none)';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export interface ProvenanceTooltipProps {
  provenance: ProvenanceMap | null | undefined;
  fieldKey: string;
  staleThresholdDays: number;
  children: ReactNode;
  mode: 'inline' | 'cell' | 'chip';
  /** R3 Sol-lock: omit stale chip/icon while preserving tooltip body. */
  suppressStale?: boolean;
  /** Custom formatter for the override-chain "Was: <value>" line. V1 default
   *  handles number/boolean/string generically; field-specific formatters
   *  (e.g. `fmt$` for currency) pass-in from consumer. */
  previousValueFormatter?: (value: unknown) => string;
  /** Day 84.8b P1 Lock 2 — value is snapshotted at decision-time but the
   *  source flag reflects current product_profitability cache state, not
   *  at-snapshot-time. Tooltip body prepends "Current source:" prefix to
   *  signal the divergence. Default false preserves current-state-surface
   *  rendering (Restock / BuyingSession / DetailPanel / POLineItemsGrid). */
  valueIsSnapshot?: boolean;
}

const HOVER_DELAY_MS = 500; // UI_STANDARDS §20

export function ProvenanceTooltip({
  provenance,
  fieldKey,
  staleThresholdDays,
  children,
  mode,
  suppressStale = false,
  previousValueFormatter = defaultPreviousValueFormatter,
  valueIsSnapshot = false,
}: ProvenanceTooltipProps): JSX.Element {
  const ind = useMemo(
    () => deriveProvenanceIndicator(provenance, fieldKey, staleThresholdDays),
    [provenance, fieldKey, staleThresholdDays]
  );

  const showStale = ind.stale && !suppressStale;

  // Assemble tooltip body lines per L2 §1-4. Stale chip (line §5) rendered
  // separately via showStale state, not as a tooltip-body line.
  const lines = useMemo<string[]>(() => {
    if (!ind.source && !ind.setAt && !ind.hasOverrideChain && !ind.reason) {
      return [];
    }
    const out: string[] = [];
    out.push(sourcePrefix(ind.source, valueIsSnapshot));
    if (ind.setAt) out.push(`Updated ${relativeTimeShort(ind.setAt)}`);
    if (ind.hasOverrideChain) {
      out.push(`Was: ${previousValueFormatter(ind.previousValue)}`);
    }
    if (ind.reason) out.push(`Reason: ${ind.reason}`);
    return out;
  }, [ind, previousValueFormatter, valueIsSnapshot]);

  // ── Cell mode: native title= for AG Grid perf — no React hover state ─────
  if (mode === 'cell') {
    return (
      <span
        className={clsx('inline-flex items-center gap-1', ind.italic && 'italic')}
        title={lines.length > 0 ? lines.join('\n') : undefined}
        data-provenance-mode="cell"
      >
        {children}
        {showStale && <Clock size={12} className="text-warning flex-shrink-0" aria-label="Stale" />}
      </span>
    );
  }

  // ── Inline / chip modes share the hover-tooltip surface ──────────────────
  return (
    <HoverTooltipWrapper
      lines={lines}
      italic={ind.italic}
      showInlineStaleIcon={mode === 'inline' && showStale}
      showStaleChip={mode === 'chip' && showStale}
    >
      {children}
    </HoverTooltipWrapper>
  );
}

interface HoverTooltipWrapperProps {
  lines: string[];
  italic: boolean;
  showInlineStaleIcon: boolean;
  showStaleChip: boolean;
  children: ReactNode;
}

function HoverTooltipWrapper({
  lines,
  italic,
  showInlineStaleIcon,
  showStaleChip,
  children,
}: HoverTooltipWrapperProps): JSX.Element {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  function handleEnter(): void {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left });
      }
      setShow(true);
      timeoutRef.current = null;
    }, HOVER_DELAY_MS);
  }

  function handleLeave(): void {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShow(false);
  }

  const hasTooltip = lines.length > 0;

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      className="inline-flex items-center gap-1 relative"
    >
      <span className={clsx(italic && 'italic')}>{children}</span>
      {showInlineStaleIcon && (
        <Clock size={12} className="text-warning flex-shrink-0" aria-label="Stale" />
      )}
      {showStaleChip && (
        <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/15 text-warning flex-shrink-0">
          Stale
        </span>
      )}
      {show && hasTooltip && (
        <span
          role="tooltip"
          className="fixed z-[var(--z-tooltip,400)] max-w-[200px] px-2 py-1 rounded
                     bg-surface-4 border border-border text-xs text-ink-bright
                     shadow-lg pointer-events-none"
          style={{ top: pos.top, left: pos.left }}
        >
          {lines.map((line, i) => (
            <span key={i} className="block">
              {line}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
